/**
 * Cart Upsell Step — asset: cart-upsell-step.js
 *
 * Flow:
 *  1. User clicks "Checkout Securely" in the cart drawer
 *  2. Intercept checkout intent
 *  3. Open configured upsell UI variant (A or B)
 *  4. Accept  -> add selected variant -> continue checkout
 *     Decline -> continue checkout
 */
(function () {
  'use strict';

  var CONFIG = window.CartUpsellStepConfig;
  if (!CONFIG || !CONFIG.enabled || (!CONFIG.upsell1ProductHandle && !CONFIG.upsell2ProductHandle)) return;
  var TRANSLATIONS = CONFIG.translations || {};

  var productDataByVariant = {
    upsell1: null,
    upsell2: null
  };
  var productRequestByVariant = {
    upsell1: null,
    upsell2: null
  };
  var productHandleLoadedByVariant = {
    upsell1: '',
    upsell2: ''
  };
  var productCacheByHandle = {};
  var productRequestByHandle = {};
  var cachedCartFallbackDecision = null;
  var cachedCartSnapshot = null;
  var cachedCartFallbackDecisionAt = 0;
  var cartDecisionRequest = null;
  var cartDecisionRequestGeneration = -1;
  var cartDecisionGeneration = 0;
  var productSubmitDecision = null;
  var productSubmitDecisionAt = 0;
  var awaitingPostSubmitCartRefresh = false;
  var cartDrawerObserver = null;
  var selectedVariantIdByVariant = {
    upsell1: '',
    upsell2: ''
  };
  var activeUiVariant = 'upsell1';
  var activeOffer = createGlobalOffer();

  var refs = {};
  var hasBoundEvents = false;
  var hideTimer = null;
  var offerTimerInterval = null;
  var scarcityProgressRaf = null;
  var socialProofInterval = null;
  var orderCountInterval = null;
  var lastFocusedElement = null;
  var showRequestId = 0;
  var ANON_ID_STORAGE_KEY = 'cu-step-ab-anon-id';
  var DEBUG_VARIANT_STORAGE_KEY = 'cu-step-debug-ui-variant';
  var CHECKOUT_FAIL_OPEN_TIMEOUT_MS = 1000;
  var CART_FALLBACK_CACHE_TTL_MS = 15000;

  var SOCIAL_PROOFS = [
    { name: 'John', location: 'Texas', minutes: 2 },
    { name: 'Mike', location: 'Ohio', minutes: 4 },
    { name: 'Jake', location: 'Colorado', minutes: 1 },
    { name: 'David', location: 'Georgia', minutes: 6 },
    { name: 'Matt', location: 'Montana', minutes: 3 },
    { name: 'Chris', location: 'Florida', minutes: 5 },
    { name: 'Kevin', location: 'Arizona', minutes: 2 },
    { name: 'Brian', location: 'Tennessee', minutes: 7 },
    { name: 'Jason', location: 'California', minutes: 3 },
    { name: 'Nick', location: 'Michigan', minutes: 8 },
    { name: 'Ryan', location: 'North Carolina', minutes: 4 },
    { name: 'Tyler', location: 'Pennsylvania', minutes: 2 },
    { name: 'Brandon', location: 'Illinois', minutes: 6 },
    { name: 'Andrew', location: 'Virginia', minutes: 5 },
    { name: 'Zach', location: 'Missouri', minutes: 3 },
    { name: 'Josh', location: 'Indiana', minutes: 7 },
    { name: 'Ethan', location: 'South Carolina', minutes: 1 },
    { name: 'Adam', location: 'Wisconsin', minutes: 4 },
    { name: 'Luke', location: 'Alabama', minutes: 2 },
    { name: 'Nathan', location: 'Kentucky', minutes: 5 },
    { name: 'Sean', location: 'New Jersey', minutes: 6 }
  ];
  var SCARCITY_BASE_PERCENT = 73;

  function $(sel, ctx) {
    return (ctx || document).querySelector(sel);
  }

  function translated(key, fallback) {
    var value = TRANSLATIONS[key];
    return typeof value === 'string' && value.trim() ? value : fallback;
  }

  function normalizeUiVariant(value) {
    var normalized = String(value || '').toLowerCase().trim();
    return normalized === 'upsell2' || normalized === 'b' ? 'upsell2' : 'upsell1';
  }

  function createGlobalOffer() {
    var globalSettings = CONFIG.globalSettings || {};

    return {
      type: 'global',
      fallbackBehavior: 'global',
      uiVariant: CONFIG.uiVariant || 'upsell1',
      enabledByVariant: {
        upsell1: !!CONFIG.upsell1Enabled,
        upsell2: !!CONFIG.upsell2Enabled
      },
      productHandleByVariant: {
        upsell1: CONFIG.upsell1ProductHandle || '',
        upsell2: CONFIG.upsell2ProductHandle || ''
      },
      productIdByVariant: {
        upsell1: CONFIG.upsell1ProductId || '',
        upsell2: CONFIG.upsell2ProductId || ''
      },
      moneyMapByVariant: {
        upsell1: CONFIG.upsell1VariantMoneyMap || {},
        upsell2: CONFIG.upsell2VariantMoneyMap || {}
      },
      shared: globalSettings.shared || {},
      settingsByVariant: {
        upsell1: globalSettings.upsell1 || {},
        upsell2: globalSettings.upsell2 || {}
      }
    };
  }

  function setActiveOffer(nextOffer) {
    var previous = activeOffer || createGlobalOffer();
    var resolvedOffer = nextOffer || createGlobalOffer();

    ['upsell1', 'upsell2'].forEach(function (uiVariant) {
      var oldHandle = previous.productHandleByVariant && previous.productHandleByVariant[uiVariant];
      var nextHandle = resolvedOffer.productHandleByVariant && resolvedOffer.productHandleByVariant[uiVariant];
      if (oldHandle !== nextHandle) {
        selectedVariantIdByVariant[uiVariant] = '';
      }
    });

    activeOffer = resolvedOffer;
  }

  function resetActiveOfferToGlobal() {
    setActiveOffer(createGlobalOffer());
  }

  function getOfferUiSettings(uiVariant) {
    var resolved = normalizeUiVariant(uiVariant || activeUiVariant);
    if (!activeOffer || !activeOffer.settingsByVariant) return {};
    return activeOffer.settingsByVariant[resolved] || {};
  }

  function getOfferSetting(uiVariant, key) {
    var settings = getOfferUiSettings(uiVariant);
    if (Object.prototype.hasOwnProperty.call(settings, key)) {
      return settings[key];
    }
    return '';
  }

  function getOfferSharedSetting(key, legacyKey) {
    var shared = (activeOffer && activeOffer.shared) ? activeOffer.shared : {};
    if (Object.prototype.hasOwnProperty.call(shared, key)) {
      return shared[key];
    }

    var globalShared = (CONFIG.globalSettings && CONFIG.globalSettings.shared) ? CONFIG.globalSettings.shared : {};
    if (Object.prototype.hasOwnProperty.call(globalShared, key)) {
      return globalShared[key];
    }

    return legacyKey ? CONFIG[legacyKey] : CONFIG[key];
  }

  function normalizeSessionKeyPart(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/[^a-z0-9_-]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'unknown';
  }

  function isTemplateOfferActive() {
    return !!(activeOffer && activeOffer.type === 'template');
  }

  function isVariantEnabled(value) {
    var variantName = normalizeUiVariant(value);
    var offer = activeOffer || createGlobalOffer();
    var enabledByVariant = offer.enabledByVariant || {};
    var productHandleByVariant = offer.productHandleByVariant || {};

    return !!(enabledByVariant[variantName] && productHandleByVariant[variantName]);
  }

  function getFallbackUiVariant(value) {
    var variantName = normalizeUiVariant(value);
    return variantName === 'upsell2' ? 'upsell1' : 'upsell2';
  }

  function resolveUsableUiVariant(value) {
    var variantName = normalizeUiVariant(value);
    if (isVariantEnabled(variantName)) return variantName;

    var fallback = getFallbackUiVariant(variantName);
    if (isVariantEnabled(fallback)) return fallback;

    return variantName;
  }

  function getProductHandleForVariant(value) {
    var variantName = normalizeUiVariant(value);
    var offer = activeOffer || createGlobalOffer();
    var productHandleByVariant = offer.productHandleByVariant || {};
    return productHandleByVariant[variantName] || '';
  }

  function getProductDataForVariant(value) {
    return productDataByVariant[normalizeUiVariant(value)];
  }

  function getActiveProductData() {
    return getProductDataForVariant(activeUiVariant);
  }

  function getStoredVariantId(value) {
    return selectedVariantIdByVariant[normalizeUiVariant(value)] || '';
  }

  function setStoredVariantId(value, variantId) {
    selectedVariantIdByVariant[normalizeUiVariant(value)] = variantId ? String(variantId) : '';
  }

  function generateAnonId() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }

    return 'anon-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
  }

  function getAnonId() {
    try {
      var existing = localStorage.getItem(ANON_ID_STORAGE_KEY);
      if (existing) return existing;

      var created = generateAnonId();
      localStorage.setItem(ANON_ID_STORAGE_KEY, created);
      return created;
    } catch (_) {
      return '';
    }
  }

  function simpleHash(str) {
    var input = String(str || '');
    var h = 0;

    for (var i = 0; i < input.length; i += 1) {
      h = ((h << 5) - h) + input.charCodeAt(i);
      h |= 0;
    }

    return h >>> 0;
  }

  function getAssignedUiVariant() {
    var experimentName = asTrimmedText(CONFIG.experimentName) || 'cart-upsell-step-ui-v1';
    var anonId = getAnonId();
    if (!anonId) {
      return resolveUsableUiVariant(CONFIG.uiVariant || 'upsell1');
    }

    var bucket = simpleHash(experimentName + ':' + anonId) % 100;
    return resolveUsableUiVariant(bucket < 50 ? 'upsell1' : 'upsell2');
  }

  function readUiVariantFromUrl() {
    try {
      var raw = new URLSearchParams(window.location.search).get('cu_step_variant');
      if (!raw) return '';
      return normalizeUiVariant(String(raw).toLowerCase());
    } catch (_) {
      return '';
    }
  }

  function readDebugUiVariantFromStorage() {
    try {
      var raw = localStorage.getItem(DEBUG_VARIANT_STORAGE_KEY);
      if (!raw) return '';
      return normalizeUiVariant(String(raw).toLowerCase());
    } catch (_) {
      return '';
    }
  }

  function getPreferredUiVariant() {
    if (isTemplateOfferActive()) {
      return resolveUsableUiVariant(activeOffer.uiVariant || 'upsell1');
    }

    var fromUrl = readUiVariantFromUrl();
    if (fromUrl) return resolveUsableUiVariant(fromUrl);

    if (shouldShowDevToggle()) {
      var fromStorage = readDebugUiVariantFromStorage();
      if (fromStorage) return resolveUsableUiVariant(fromStorage);
    }

    return getAssignedUiVariant();
  }

  function persistDebugUiVariant(value) {
    try {
      localStorage.setItem(DEBUG_VARIANT_STORAGE_KEY, normalizeUiVariant(value));
    } catch (_) {}
  }

  function shouldShowDevToggle() {
    return !!CONFIG.debugSwitcherEnabled && !isTemplateOfferActive();
  }

  function refreshDevToggleUi() {
    var toggle = $('#cu-step-dev-toggle');
    if (!toggle) return;

    toggle.hidden = !shouldShowDevToggle();

    var buttons = toggle.querySelectorAll('[data-cu-variant-choice]');
    buttons.forEach(function (btn) {
      var v = normalizeUiVariant(btn.getAttribute('data-cu-variant-choice') || 'upsell1');
      btn.disabled = !isVariantEnabled(v);
      btn.classList.toggle('is-active', v === activeUiVariant);
    });
  }

  function getMoneyFormat() {
    var configured = asTrimmedText(CONFIG.moneyFormat);
    return configured || '${{amount}}';
  }

  function formatWithDelimiters(number, precision, thousands, decimal) {
    var decimals = precision == null ? 2 : precision;
    var thousandsSeparator = thousands == null ? ',' : thousands;
    var decimalSeparator = decimal == null ? '.' : decimal;
    var value = Number(number || 0);

    if (!Number.isFinite(value)) value = 0;

    var fixed = value.toFixed(decimals);
    var parts = fixed.split('.');
    var dollars = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSeparator);
    var cents = parts[1] ? decimalSeparator + parts[1] : '';
    return dollars + cents;
  }

  function formatMoney(cents) {
    if (cents == null || cents === '') return '';

    var centsValue = Number(cents);
    if (!Number.isFinite(centsValue)) return '';

    var format = getMoneyFormat();
    var formatString = /\{\{\s*(\w+)\s*\}\}/;
    var placeholderMatch = format.match(formatString);
    var placeholder = placeholderMatch ? placeholderMatch[1] : 'amount';
    var amount = '';

    switch (placeholder) {
      case 'amount':
        amount = formatWithDelimiters(centsValue / 100, 2, ',', '.');
        break;
      case 'amount_no_decimals':
        amount = formatWithDelimiters(centsValue / 100, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        amount = formatWithDelimiters(centsValue / 100, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        amount = formatWithDelimiters(centsValue / 100, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        amount = formatWithDelimiters(centsValue / 100, 2, "'", '.');
        break;
      case 'amount_no_decimals_with_space_separator':
        amount = formatWithDelimiters(centsValue / 100, 0, ' ', '.');
        break;
      case 'amount_with_space_separator':
        amount = formatWithDelimiters(centsValue / 100, 2, ' ', '.');
        break;
      default:
        amount = formatWithDelimiters(centsValue / 100, 2, ',', '.');
    }

    return format.replace(formatString, amount);
  }

  function asTrimmedText(value) {
    if (value == null) return '';
    return String(value).trim();
  }

  function getSessionKeyForOffer(offer) {
    var baseKey = (CONFIG && CONFIG.sessionKey) ? CONFIG.sessionKey : 'cu-step-shown-v2';
    var resolvedOffer = offer || createGlobalOffer();
    var uiVariant = normalizeUiVariant((resolvedOffer && resolvedOffer.uiVariant) || activeUiVariant);
    var productHandleByVariant = resolvedOffer.productHandleByVariant || {};
    var productIdByVariant = resolvedOffer.productIdByVariant || {};
    var productKey = productIdByVariant[uiVariant] || productHandleByVariant[uiVariant] || '';

    if (!resolvedOffer || resolvedOffer.type !== 'template') {
      return [
        baseKey,
        'global',
        uiVariant,
        normalizeSessionKeyPart(productHandleByVariant[uiVariant])
      ].join(':');
    }

    return [
      baseKey,
      'template',
      normalizeSessionKeyPart(resolvedOffer.sourceProductId || resolvedOffer.sourceProductHandle),
      uiVariant,
      normalizeSessionKeyPart(productKey)
    ].join(':');
  }

  function getSessionKey() {
    return getSessionKeyForOffer(activeOffer || createGlobalOffer());
  }

  function shouldOfferUseOncePerSession(offer) {
    var resolvedOffer = offer || createGlobalOffer();
    var shared = resolvedOffer.shared || {};

    if (Object.prototype.hasOwnProperty.call(shared, 'oncePerSession')) {
      return !!shared.oncePerSession;
    }

    return !!CONFIG.oncePerSession;
  }

  function shouldUseOncePerSession() {
    return shouldOfferUseOncePerSession(activeOffer || createGlobalOffer());
  }

  function wasOfferHandledForSession(offer) {
    if (!shouldOfferUseOncePerSession(offer)) return false;
    try {
      return sessionStorage.getItem(getSessionKeyForOffer(offer)) === '1';
    } catch (_) {
      return false;
    }
  }

  function wasUpsellHandledForSession() {
    return wasOfferHandledForSession(activeOffer || createGlobalOffer());
  }

  function markUpsellHandledForSession() {
    if (!shouldUseOncePerSession()) return;
    try {
      sessionStorage.setItem(getSessionKey(), '1');
    } catch (_) {}
  }

  function formatTimerValue(totalSeconds) {
    var safe = Math.max(0, totalSeconds | 0);
    var mins = Math.floor(safe / 60);
    var secs = safe % 60;
    return String(mins).padStart(2, '0') + ':' + String(secs).padStart(2, '0');
  }

  function stopOfferTimer() {
    if (offerTimerInterval) {
      clearInterval(offerTimerInterval);
      offerTimerInterval = null;
    }
  }

  function startOfferTimer() {
    stopOfferTimer();
    if (!refs.timerEl) return;

    var total = parseInt(getOfferSharedSetting('timerSeconds', 'timerSeconds'), 10);
    if (!total || total < 0) {
      refs.timerEl.textContent = '00:00';
      setScarcityProgress(0, 0);
      return;
    }

    var remaining = total;
    refs.timerEl.textContent = formatTimerValue(remaining);
    startScarcityProgressAnimation(remaining, total);

    offerTimerInterval = setInterval(function () {
      remaining -= 1;
      if (remaining <= 0) {
        refs.timerEl.textContent = '00:00';
        setScarcityProgress(0, total);
        stopOfferTimer();
        return;
      }
      refs.timerEl.textContent = formatTimerValue(remaining);
    }, 1000);
  }

  function stopSocialProofTicker() {
    if (socialProofInterval) {
      clearInterval(socialProofInterval);
      socialProofInterval = null;
    }
  }

  function formatSocialProof(proof) {
    var template = translated('socialProof', '[name] from [location] ordered this deal — [minutes] min ago');
    var name = proof && proof.name ? escapeHtml(proof.name) : '';
    var location = proof && proof.location ? escapeHtml(proof.location) : '';
    var minutes = proof && proof.minutes != null ? escapeHtml(proof.minutes) : '';

    return replaceAllText(
      replaceAllText(
        replaceAllText(template, '[name]', '<strong>' + name + '</strong>'),
        '[location]',
        location
      ),
      '[minutes]',
      minutes
    );
  }

  function startSocialProofTicker() {
    stopSocialProofTicker();
    if (!refs.socialProofEl) return;

    var idx = 0;
    refs.socialProofEl.innerHTML = formatSocialProof(SOCIAL_PROOFS[0]);

    socialProofInterval = setInterval(function () {
      idx = (idx + 1) % SOCIAL_PROOFS.length;
      refs.socialProofEl.style.opacity = '0';
      setTimeout(function () {
        if (!refs.socialProofEl) return;
        refs.socialProofEl.innerHTML = formatSocialProof(SOCIAL_PROOFS[idx]);
        refs.socialProofEl.style.opacity = '1';
      }, 250);
    }, 4000);
  }

  function stopOrderCountTicker() {
    if (orderCountInterval) {
      clearInterval(orderCountInterval);
      orderCountInterval = null;
    }
  }

  function getEstDecimalHour() {
    var now = new Date();
    var utcMs = now.getTime() + now.getTimezoneOffset() * 60000;
    var est = new Date(utcMs - 5 * 3600000);
    return est.getHours() + est.getMinutes() / 60 + est.getSeconds() / 3600;
  }

  function calcOrders() {
    var h = getEstDecimalHour();
    var dailyMax = 1487;
    var progress = 0;

    if (h < 6) progress = 0.01 + (h / 6) * 0.04;
    else if (h < 12) progress = 0.05 + ((h - 6) / 6) * 0.32;
    else if (h < 18) progress = 0.37 + ((h - 12) / 6) * 0.44;
    else if (h < 22) progress = 0.81 + ((h - 18) / 4) * 0.16;
    else progress = 0.97 + ((h - 22) / 2) * 0.03;

    var now = new Date();
    var minuteOfDay = now.getUTCHours() * 60 + now.getUTCMinutes();
    var jitter = (minuteOfDay % 11) * 1.1;

    return Math.min(dailyMax, Math.round(dailyMax * progress + jitter));
  }

  function updateOrderCount() {
    if (!refs.orderCountEl) return;
    refs.orderCountEl.textContent = calcOrders().toLocaleString();
  }

  function startOrderCountTicker() {
    stopOrderCountTicker();
    if (!refs.orderCountEl) return;
    updateOrderCount();
    orderCountInterval = setInterval(updateOrderCount, 60000);
  }

  function stopVariantEnhancements() {
    stopOfferTimer();
    stopScarcityProgressAnimation();
    stopSocialProofTicker();
    stopOrderCountTicker();
  }

  function setScarcityProgress(remaining, total) {
    var barEl = refs.scarcityBarFill || $('#cu-step-v2 .cu2-bar-fill');
    if (!barEl) return;

    var progress = 100;
    if (Number.isFinite(total) && total > 0) {
      var safeRemaining = Math.max(0, Number(remaining) || 0);
      progress = SCARCITY_BASE_PERCENT + (((total - safeRemaining) / total) * (100 - SCARCITY_BASE_PERCENT));
    } else {
      progress = SCARCITY_BASE_PERCENT;
    }
    progress = Math.max(0, Math.min(100, progress));
    barEl.style.transition = 'none';
    barEl.style.width = progress.toFixed(2) + '%';
  }

  function stopScarcityProgressAnimation() {
    if (scarcityProgressRaf) {
      cancelAnimationFrame(scarcityProgressRaf);
      scarcityProgressRaf = null;
    }
  }

  function startScarcityProgressAnimation(remaining, total) {
    stopScarcityProgressAnimation();
    var barEl = refs.scarcityBarFill || $('#cu-step-v2 .cu2-bar-fill');
    if (!barEl) return;
    if (!Number.isFinite(total) || total <= 0) {
      setScarcityProgress(0, 0);
      return;
    }

    var safeRemaining = Math.max(0, Number(remaining) || 0);
    var startProgress = SCARCITY_BASE_PERCENT + (((total - safeRemaining) / total) * (100 - SCARCITY_BASE_PERCENT));
    startProgress = Math.max(0, Math.min(100, startProgress));

    barEl.style.transition = 'none';
    barEl.style.width = startProgress.toFixed(2) + '%';
    if (safeRemaining <= 0) return;

    var durationMs = safeRemaining * 1000;
    var startedAt = performance.now();

    function step(now) {
      if (!barEl) return;

      var elapsed = Math.max(0, now - startedAt);
      var ratio = Math.min(1, elapsed / durationMs);
      var nextProgress = startProgress + (100 - startProgress) * ratio;
      barEl.style.width = nextProgress.toFixed(2) + '%';

      if (ratio >= 1) {
        scarcityProgressRaf = null;
        return;
      }

      scarcityProgressRaf = requestAnimationFrame(step);
    }

    scarcityProgressRaf = requestAnimationFrame(step);
  }

  function startVariantEnhancements() {
    startOfferTimer();
    if (activeUiVariant === 'upsell2') {
      startSocialProofTicker();
      startOrderCountTicker();
    } else {
      stopSocialProofTicker();
      stopOrderCountTicker();
    }
  }

  function resolveRefs() {
    refs = {
      root: $('#cu-step-root'),
      step: activeUiVariant === 'upsell2' ? $('#cu-step-v2') : $('#cu-step'),
      imgEl: activeUiVariant === 'upsell2' ? $('#cu2-img') : $('#cu-img'),
      titleEl: activeUiVariant === 'upsell2' ? $('#cu2-title') : $('#cu-title'),
      heroTitleEl: activeUiVariant === 'upsell2' ? null : $('#cu-hero-title'),
      ratingEl: activeUiVariant === 'upsell2' ? $('#cu2-rating-text') : $('#cu-rating-text'),
      timerLabelEl: activeUiVariant === 'upsell2' ? $('#cu2-timer-caption') : $('#cu-timer-label'),
      subtextEl: activeUiVariant === 'upsell2' ? null : $('#cu-subtext'),
      reasonsEl: activeUiVariant === 'upsell2' ? null : $('#cu-reasons-text'),
      offerHeaderEl: activeUiVariant === 'upsell2' ? $('#cu2-header-title') : null,
      shippingEl: activeUiVariant === 'upsell2' ? $('#cu2-shipping-copy') : null,
      scarcityLabelEl: activeUiVariant === 'upsell2' ? $('#cu2-scarcity-label') : null,
      scarcityCountEl: activeUiVariant === 'upsell2' ? $('#cu2-scarcity-num') : null,
      benefitsHeadingEl: activeUiVariant === 'upsell2' ? $('#cu2-benefits-heading') : null,
      benefit1El: activeUiVariant === 'upsell2' ? $('#cu2-benefit-1') : null,
      benefit2El: activeUiVariant === 'upsell2' ? $('#cu2-benefit-2') : null,
      benefit3El: activeUiVariant === 'upsell2' ? $('#cu2-benefit-3') : null,
      priceOrigLabel: activeUiVariant === 'upsell2' ? null : $('#cu-price-orig-label'),
      priceOrigEl: activeUiVariant === 'upsell2' ? $('#cu2-price-orig') : $('#cu-price-orig'),
      priceCurEl: activeUiVariant === 'upsell2' ? $('#cu2-price-cur') : $('#cu-price-cur'),
      priceSeparator: activeUiVariant === 'upsell2' ? null : $('#cu-price-separator'),
      saveAmountEl: activeUiVariant === 'upsell2' ? $('#cu2-save-amount') : null,
      variantWrap: activeUiVariant === 'upsell2' ? $('#cu2-variant-wrap') : $('#cu-variant-wrap'),
      variantLabel: activeUiVariant === 'upsell2' ? $('#cu2-variant-label') : $('#cu-variant-label'),
      variantSelect: activeUiVariant === 'upsell2' ? $('#cu2-variant-select') : $('#cu-variant-select'),
      variantButtonsWrap: activeUiVariant === 'upsell2' ? $('#cu2-variant-buttons') : null,
      acceptBtn: activeUiVariant === 'upsell2' ? $('#cu2-accept-btn') : $('#cu-accept-btn'),
      declineBtn: activeUiVariant === 'upsell2' ? $('#cu2-decline-btn') : $('#cu-decline-btn'),
      closeBtn: activeUiVariant === 'upsell2' ? $('#cu2-close-btn') : $('#cu-close-btn'),
      timerEl: activeUiVariant === 'upsell2' ? $('#cu2-timer') : $('#cu-timer'),
      orderCountEl: activeUiVariant === 'upsell2' ? $('#cu2-order-count') : null,
      socialProofEl: activeUiVariant === 'upsell2' ? $('#cu2-social-proof') : null,
      scarcityBarFill: activeUiVariant === 'upsell2' ? $('.cu2-bar-fill') : null
    };
  }

  function setActiveUiVariant(nextVariant) {
    var resolved = resolveUsableUiVariant(nextVariant);

    var wasOpen = !!(refs.root && refs.root.classList.contains('cu-root-visible'));

    activeUiVariant = resolved;

    var root = $('#cu-step-root');
    if (root) {
      root.setAttribute('data-cu-variant', resolved);
    }

    var upsell1Panel = $('#cu-step');
    var upsell2Panel = $('#cu-step-v2');

    if (upsell1Panel) {
      upsell1Panel.classList.toggle('cu-step-panel-active', resolved === 'upsell1');
      upsell1Panel.classList.remove('cu-visible');
    }

    if (upsell2Panel) {
      upsell2Panel.classList.toggle('cu-step-panel-active', resolved === 'upsell2');
      upsell2Panel.classList.remove('cu-visible');
    }

    stopVariantEnhancements();
    resolveRefs();
    refreshDevToggleUi();

    var activeProduct = getActiveProductData();
    if (activeProduct) {
      populatePanel(activeProduct, resolved);
    } else {
      preloadVariantProduct(resolved);
    }

    var selectedVariantId = getStoredVariantId(resolved);
    if (selectedVariantId) {
      applyVariantState(selectedVariantId, resolved);
    }

    if (wasOpen && refs.step) {
      refs.step.classList.add('cu-visible');
      startVariantEnhancements();
    }
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function newlineToBr(value) {
    return String(value == null ? '' : value).replace(/\r\n|\r|\n/g, '<br>');
  }

  function replaceAllText(source, search, replacement) {
    return String(source == null ? '' : source).split(search).join(replacement);
  }

  function getVariantPriceText(variant, uiVariant) {
    if (!variant) return '';
    return getFormattedVariantMoney(variant, 'price', uiVariant) || formatMoney(variant.price);
  }

  function replaceOfferPlaceholders(template, productTitle, discountText, discountHtml) {
    var output = String(template == null ? '' : template);
    output = replaceAllText(output, '[product_title]', productTitle || '');
    output = replaceAllText(output, '[discount_label]', discountHtml || discountText || '');
    output = replaceAllText(output, '60% OFF', discountHtml || discountText || '');
    return output;
  }

  function getConfiguredTemplate(uiVariant, key, fallback) {
    var configured = getOfferSetting(uiVariant, key);
    if (asTrimmedText(configured)) return configured;
    return fallback;
  }

  function getConfiguredTitleText(productTitle, uiVariant) {
    var resolved = normalizeUiVariant(uiVariant);
    var template = getConfiguredTemplate(resolved, 'titleTemplate', '[product_title]');
    return replaceAllText(template, '[product_title]', productTitle || '');
  }

  function updateTitleText(productTitle, uiVariant) {
    if (!refs.titleEl) return;
    refs.titleEl.textContent = getConfiguredTitleText(productTitle, uiVariant);
  }

  function updateRatingText(uiVariant) {
    if (!refs.ratingEl) return;

    var fallback = normalizeUiVariant(uiVariant) === 'upsell2'
      ? (CONFIG.upsell2RatingText || CONFIG.upsell1RatingText || CONFIG.ratingText)
      : (CONFIG.upsell1RatingText || CONFIG.ratingText);
    var text = asTrimmedText(getOfferSetting(uiVariant, 'ratingText')) || asTrimmedText(fallback);
    if (text) refs.ratingEl.textContent = text;
  }

  function updateVariantLabels(product, uiVariant) {
    if (!refs.variantLabel) return;

    var fallbackLabel = (product && product.options && product.options[0] && product.options[0].name) ? product.options[0].name : '';
    var text = asTrimmedText(getOfferSetting(uiVariant, 'variantLabel')) || fallbackLabel;
    refs.variantLabel.textContent = text;
  }

  function formatBenefitHtml(value) {
    var text = String(value == null ? '' : value);
    var head = text.split('. ')[0].trim();
    var tail = text.replace(head, '').trim();

    if (!head) {
      return newlineToBr(escapeHtml(text));
    }

    var html = '<strong>' + escapeHtml(head);
    if (tail) html += '.';
    html += '</strong>';

    if (tail) {
      html += ' ' + newlineToBr(escapeHtml(tail.replace(/^\./, '').trim()));
    }

    return html;
  }

  function updateOfferCopyForVariant(variant, uiVariant) {
    var resolved = normalizeUiVariant(uiVariant || activeUiVariant);
    var product = getProductDataForVariant(resolved);
    var productTitle = product && product.title ? product.title : '';
    var priceText = getVariantPriceText(variant, resolved);
    var priceStrong = '<strong>' + priceText + '</strong>';

    updateTitleText(productTitle, resolved);
    updateRatingText(resolved);

    if (refs.timerLabelEl) {
      var timerFallback = translated('timer', 'Offer expires in:');
      var timerLabel = asTrimmedText(getOfferSetting(resolved, 'timerLabel')) || timerFallback;
      refs.timerLabelEl.textContent = timerLabel;
    }

    if (resolved === 'upsell1') {
      if (refs.heroTitleEl) {
        var heroTemplate = getConfiguredTemplate(resolved, 'heroText', translated('hero', 'How about another [product_title] for [discount_label]?'));
        setNodeContent(refs.heroTitleEl, replaceOfferPlaceholders(heroTemplate, productTitle, priceText, priceStrong));
      }

      if (refs.subtextEl) {
        var subtextTemplate = getConfiguredTemplate(resolved, 'subtext', translated('subtext', 'Limited-time deal — add this to your order now and save big.'));
        var subtext = replaceOfferPlaceholders(subtextTemplate, productTitle, priceText, priceText);
        if (priceText) subtext = replaceAllText(subtext, priceText, priceStrong);

        var chooseText = asTrimmedText(getOfferSetting(resolved, 'chooseColorText'));
        if (!chooseText) chooseText = translated('chooseVariant', 'Choose your variant below.');
        if (chooseText) subtext += ' <strong>' + chooseText + '</strong>';

        setNodeContent(refs.subtextEl, subtext);
      }

      if (refs.reasonsEl) {
        var reasons = asTrimmedText(getOfferSetting(resolved, 'reasonsText'));
        if (reasons) {
          refs.reasonsEl.innerHTML = newlineToBr(escapeHtml(reasons));
          refs.reasonsEl.style.display = '';
        } else {
          refs.reasonsEl.innerHTML = '';
          refs.reasonsEl.style.display = 'none';
        }
      }

      if (refs.acceptBtn) {
        refs.acceptBtn.textContent = asTrimmedText(getOfferSetting(resolved, 'acceptText')) || translated('accept', 'Add to order');
      }
      if (refs.declineBtn) {
        refs.declineBtn.textContent = asTrimmedText(getOfferSetting(resolved, 'declineText')) || translated('decline', 'Decline this offer');
      }

      return;
    }

    if (refs.offerHeaderEl) {
      var headerTemplate = getConfiguredTemplate(resolved, 'offerHeaderText', translated('header', 'Add another [product_title] for [discount_label]?'));
      var headerHtml = replaceOfferPlaceholders(headerTemplate, productTitle, priceText, '<span id="cu2-price-inline">' + priceText + '</span>');
      if (headerTemplate.indexOf('[discount_label]') === -1) {
        headerHtml += ' <span id="cu2-price-inline">' + priceText + '</span>?';
      }
      setNodeContent(refs.offerHeaderEl, headerHtml);
    }

    if (refs.shippingEl) {
      refs.shippingEl.textContent = asTrimmedText(getOfferSetting(resolved, 'shippingText')) || translated('shipping', 'Free shipping — ships in the same order');
    }
    if (refs.scarcityLabelEl) {
      refs.scarcityLabelEl.textContent = asTrimmedText(getOfferSetting(resolved, 'scarcityLabel')) || translated('scarcityLabel', 'Stock remaining at this price');
    }
    if (refs.scarcityCountEl) {
      refs.scarcityCountEl.textContent = asTrimmedText(getOfferSetting(resolved, 'scarcityCount')) || translated('scarcityCount', '⚠️ Only 7 left');
    }
    if (refs.benefitsHeadingEl) {
      refs.benefitsHeadingEl.textContent = asTrimmedText(getOfferSetting(resolved, 'benefitsHeading')) || translated('benefitsHeading', 'Why shoppers love this deal');
    }
    if (refs.benefit1El) {
      refs.benefit1El.innerHTML = formatBenefitHtml(getConfiguredTemplate(resolved, 'benefit1', translated('benefit1', 'Great for gifting. Add one more for family, a friend, or as a backup.')));
    }
    if (refs.benefit2El) {
      refs.benefit2El.innerHTML = formatBenefitHtml(getConfiguredTemplate(resolved, 'benefit2', translated('benefit2', 'Use it every day. Most customers who buy one come back for another.')));
    }
    if (refs.benefit3El) {
      refs.benefit3El.innerHTML = formatBenefitHtml(getConfiguredTemplate(resolved, 'benefit3', translated('benefit3', 'Limited-time price. This checkout-only offer is removed after you continue.')));
    }
    if (refs.acceptBtn) {
      refs.acceptBtn.textContent = '🔒 ' + (asTrimmedText(getOfferSetting(resolved, 'acceptText')) || translated('accept', 'Add to order'));
    }
  }

  function getImageWidthForVariant(uiVariant) {
    return normalizeUiVariant(uiVariant) === 'upsell2' ? 180 : 300;
  }

  function imageUrlWithWidth(src) {
    if (!src) return '';

    try {
      var url = new URL(src, window.location.protocol);
      url.searchParams.set('width', '500');
      return url.href;
    } catch (_) {
      return src;
    }
  }

  function updateImageForVariant(variant, uiVariant) {
    var product = getProductDataForVariant(uiVariant);
    var variantImageData = variant && variant.featured_image ? variant.featured_image : null;
    var variantImage = variantImageData && variantImageData.src ? variantImageData.src : null;
    var fallback = product && product.images && product.images[0] ? product.images[0] : '';
    var newSrc = imageUrlWithWidth(variantImage || fallback);
    var imgEl = refs.imgEl;
    if (!imgEl || !newSrc) return;
    if (imgEl.src === newSrc && imgEl.alt === (product && product.title ? product.title : '')) return;

    var displayWidth = getImageWidthForVariant(uiVariant);
    imgEl.setAttribute('width', String(displayWidth));
    if (variantImageData && variantImageData.width && variantImageData.height) {
      imgEl.setAttribute('height', String(Math.ceil(displayWidth / (variantImageData.width / variantImageData.height))));
    } else {
      imgEl.setAttribute('height', String(displayWidth));
    }

    imgEl.style.opacity = '0';
    imgEl.src = newSrc;
    imgEl.alt = product && product.title ? product.title : '';

    var fadeIn = function () {
      imgEl.style.opacity = '1';
    };

    if (imgEl.complete) {
      requestAnimationFrame(fadeIn);
    } else {
      imgEl.onload = fadeIn;
    }
  }

  function getVariantMoneyMap(uiVariant) {
    var resolved = normalizeUiVariant(uiVariant || activeUiVariant);
    var offer = activeOffer || createGlobalOffer();
    var moneyMapByVariant = offer.moneyMapByVariant || {};
    return moneyMapByVariant[resolved] || {};
  }

  function getFormattedVariantMoney(variant, key, uiVariant) {
    if (!variant || !key) return '';
    var variantMoneyMap = getVariantMoneyMap(uiVariant);
    if (!variantMoneyMap) return '';

    var entry = variantMoneyMap[String(variant.id)];
    if (!entry || typeof entry !== 'object') return '';

    return asTrimmedText(entry[key]);
  }

  function containsHtml(value) {
    return /<[^>]+>/.test(String(value || ''));
  }

  function setNodeContent(node, value) {
    if (!node) return;

    var content = value == null ? '' : String(value);
    if (containsHtml(content)) {
      node.innerHTML = content;
    } else {
      node.textContent = content;
    }
  }

  function refreshConvertedMoney() {
    if (!window.bucksCC || typeof window.bucksCC.reConvert !== 'function') return;

    try {
      window.bucksCC.reConvert();
    } catch (_) {}
  }

  function updatePriceRowForVariant(variant) {
    if (!variant) return;

    if (refs.priceCurEl) {
      var basePriceText = getFormattedVariantMoney(variant, 'price') || formatMoney(variant.price);
      setNodeContent(refs.priceCurEl, basePriceText);
    }

    var upsell2InlinePrice = activeUiVariant === 'upsell2' ? $('#cu2-price-inline') : null;
    if (upsell2InlinePrice) {
      setNodeContent(upsell2InlinePrice, getFormattedVariantMoney(variant, 'price') || formatMoney(variant.price));
    }

    var configuredOriginalPrice = asTrimmedText(getOfferSharedSetting('originalPrice', 'originalPrice'));
    var hasOriginalPrice = false;
    var originalPriceText = '';

    if (configuredOriginalPrice) {
      hasOriginalPrice = true;
      originalPriceText = configuredOriginalPrice;
    } else if (variant.compare_at_price && variant.compare_at_price > variant.price) {
      hasOriginalPrice = true;
      originalPriceText = getFormattedVariantMoney(variant, 'compareAt') || formatMoney(variant.compare_at_price);
    }

    if (refs.priceOrigEl) {
      if (hasOriginalPrice) {
        setNodeContent(refs.priceOrigEl, originalPriceText);
        refs.priceOrigEl.style.display = '';
      } else {
        refs.priceOrigEl.style.display = 'none';
      }
    }

    if (refs.priceOrigLabel) {
      refs.priceOrigLabel.style.display = hasOriginalPrice ? '' : 'none';
    }

    if (refs.priceSeparator) {
      refs.priceSeparator.style.display = hasOriginalPrice ? '' : 'none';
    }

    var saveText = '';
    if (variant.compare_at_price && variant.compare_at_price > variant.price) {
      var formattedSaveAmount = getFormattedVariantMoney(variant, 'save') || formatMoney(variant.compare_at_price - variant.price);
      saveText = replaceAllText(translated('youSave', 'You save [amount]'), '[amount]', formattedSaveAmount);
    }

    if (refs.saveAmountEl) {
      setNodeContent(refs.saveAmountEl, saveText);
      refs.saveAmountEl.style.display = saveText ? '' : 'none';
    }

    var saveBadge = activeUiVariant === 'upsell2' ? $('#cu2-save-badge') : null;
    if (saveBadge) {
      if (saveText) {
        setNodeContent(saveBadge, replaceAllText(translated('save', 'Save [amount]'), '[amount]', formattedSaveAmount));
        saveBadge.style.display = '';
      } else {
        saveBadge.style.display = 'none';
      }
    }

    refreshConvertedMoney();
  }

  function getVariantById(variantId, uiVariant) {
    var product = getProductDataForVariant(uiVariant || activeUiVariant);
    if (!product || !product.variants || !variantId) return null;
    var wanted = String(variantId);

    for (var i = 0; i < product.variants.length; i += 1) {
      if (String(product.variants[i].id) === wanted) return product.variants[i];
    }

    return null;
  }

  function syncVariantControls(variant) {
    if (!variant) return;

    var wantedId = String(variant.id);

    if (refs.variantSelect) {
      refs.variantSelect.value = wantedId;
    }

    if (refs.variantButtonsWrap) {
      var buttons = refs.variantButtonsWrap.querySelectorAll('[data-cu-variant-id]');
      buttons.forEach(function (btn) {
        btn.classList.toggle('active', btn.getAttribute('data-cu-variant-id') === wantedId);
      });
    }

    if (refs.acceptBtn && !refs.acceptBtn.classList.contains('cu-loading')) {
      refs.acceptBtn.disabled = !variant.available;
    }
  }

  function applyVariantState(variantId, uiVariant) {
    var resolved = normalizeUiVariant(uiVariant || activeUiVariant);
    var variant = getVariantById(variantId, resolved);
    if (!variant) return;

    setStoredVariantId(resolved, variant.id);

    if (resolved !== activeUiVariant) {
      return;
    }

    updatePriceRowForVariant(variant);
    updateImageForVariant(variant, resolved);
    updateOfferCopyForVariant(variant, resolved);
    syncVariantControls(variant);
  }

  function buildVariantControls(product, uiVariant) {
    if (!product || normalizeUiVariant(uiVariant) !== activeUiVariant) return;

    var variants = product && product.variants ? product.variants : [];
    if (!variants.length) return;

    var firstAvailableId = '';

    if (refs.variantSelect) {
      refs.variantSelect.innerHTML = '';
      variants.forEach(function (variant) {
        var opt = document.createElement('option');
        opt.value = String(variant.id);
        opt.textContent = variant.title;
        opt.disabled = !variant.available;
        refs.variantSelect.appendChild(opt);

        if (!firstAvailableId && variant.available) {
          firstAvailableId = String(variant.id);
        }
      });
    }

    if (refs.variantButtonsWrap) {
      refs.variantButtonsWrap.innerHTML = '';
      variants.forEach(function (variant) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'cu2-v-btn';
        btn.textContent = variant.title;
        btn.setAttribute('data-cu-variant-id', String(variant.id));
        btn.disabled = !variant.available;
        btn.addEventListener('click', function () {
          applyVariantState(String(variant.id), uiVariant);
        });
        refs.variantButtonsWrap.appendChild(btn);
      });
    }

    var hasMultiple = variants.length > 1;
    if (activeUiVariant === 'upsell1' && refs.variantWrap) {
      refs.variantWrap.style.display = hasMultiple ? '' : 'none';
    }
    if (activeUiVariant === 'upsell2' && refs.variantWrap) {
      refs.variantWrap.classList.toggle('cu2-hidden', !hasMultiple);
    }

    updateVariantLabels(product, uiVariant);

    if (!firstAvailableId) {
      firstAvailableId = String(variants[0].id);
    }

    var preferredVariantId = getStoredVariantId(uiVariant);
    if (!getVariantById(preferredVariantId, uiVariant)) {
      preferredVariantId = firstAvailableId;
    }

    applyVariantState(preferredVariantId, uiVariant);
  }

  function fetchProduct(handle) {
    if (!handle) return Promise.resolve(null);

    if (productCacheByHandle[handle]) {
      return Promise.resolve(productCacheByHandle[handle]);
    }

    if (productRequestByHandle[handle]) {
      return productRequestByHandle[handle];
    }

    productRequestByHandle[handle] = fetch('/products/' + handle + '.js')
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        if (data) productCacheByHandle[handle] = data;
        productRequestByHandle[handle] = null;
        return data;
      })
      .catch(function () {
        productRequestByHandle[handle] = null;
        return null;
      });

    return productRequestByHandle[handle];
  }

  function preloadProductImages(product) {
    if (!product || !product.variants) return;

    product.variants.forEach(function (variant) {
      var imgSrc = variant.featured_image && variant.featured_image.src ? variant.featured_image.src : null;
      if (!imgSrc) return;
      var preload = new Image();
      preload.src = imgSrc;
    });
  }

  function preloadVariantProduct(uiVariant) {
    var resolved = normalizeUiVariant(uiVariant);
    var handle = getProductHandleForVariant(resolved);
    if (!handle) return Promise.resolve(null);

    if (productDataByVariant[resolved] && productHandleLoadedByVariant[resolved] === handle) {
      return Promise.resolve(productDataByVariant[resolved]);
    }

    if (productRequestByVariant[resolved] && productHandleLoadedByVariant[resolved] === handle) {
      return productRequestByVariant[resolved];
    }

    productHandleLoadedByVariant[resolved] = handle;

    productRequestByVariant[resolved] = fetchProduct(handle)
      .then(function (data) {
        if (productHandleLoadedByVariant[resolved] !== handle) {
          return null;
        }

        productDataByVariant[resolved] = data;
        preloadProductImages(data);

        if (data && resolved === activeUiVariant) {
          populatePanel(data, resolved);
        }

        productRequestByVariant[resolved] = null;
        return data;
      })
      .catch(function () {
        productRequestByVariant[resolved] = null;
        return null;
      });

    return productRequestByVariant[resolved];
  }

  function populatePanel(product, uiVariant) {
    if (!product) return;
    if (normalizeUiVariant(uiVariant) !== activeUiVariant) return;

    updateTitleText(product.title, uiVariant);
    updateRatingText(uiVariant);

    buildVariantControls(product, uiVariant);
  }

  function addVariantToCart(variantId) {
    var config = (typeof fetchConfig === 'function') ? fetchConfig('javascript') : {
      method: 'POST',
      headers: { Accept: 'application/javascript' }
    };

    config.headers = config.headers || {};
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    var formData = new FormData();
    formData.append('id', String(variantId));
    formData.append('quantity', '1');

    var drawer = $('cart-drawer');
    if (drawer && typeof drawer.getSectionsToRender === 'function') {
      formData.append('sections', drawer.getSectionsToRender().map(function (section) { return section.id; }));
      formData.append('sections_url', window.location.pathname);
    }

    config.body = formData;

    var cartAddUrl = (window.routes && window.routes.cart_add_url) ? window.routes.cart_add_url : '/cart/add';

    return fetch(cartAddUrl, config)
      .then(function (r) {
        return r.json().catch(function () { return null; });
      })
      .then(function (payload) {
        if (!payload) throw new Error('Unable to add selected variant');
        if (payload.status) throw new Error(payload.description || 'Unable to add selected variant');
        return payload;
      })
      .then(function (payload) {
        if (drawer && payload.sections && typeof drawer.renderContents === 'function') {
          drawer.renderContents(payload);
          return payload;
        }
        return refreshCartDrawerUI().then(function () { return payload; });
      })
      .catch(function (err) {
        return refreshCartDrawerUI()
          .then(function () { throw err; })
          .catch(function () { throw err; });
      });
  }

  function setAcceptLoading(isLoading) {
    ['upsell1', 'upsell2'].forEach(function (uiVariant) {
      var btn = $(uiVariant === 'upsell2' ? '#cu2-accept-btn' : '#cu-accept-btn');
      if (!btn) return;

      if (isLoading) {
        btn.disabled = true;
        btn.classList.add('cu-loading');
        return;
      }

      btn.classList.remove('cu-loading');

      var current = getVariantById(getStoredVariantId(uiVariant), uiVariant);
      btn.disabled = !!(current && !current.available);
    });
  }

  function fetchCartJson() {
    return fetch('/cart.json', { headers: { Accept: 'application/json' } })
      .then(function (response) { return response.ok ? response.json() : null; })
      .catch(function () { return null; });
  }

  function getNow() {
    return Date.now ? Date.now() : new Date().getTime();
  }

  function parseLineConfig(lineItem) {
    if (!lineItem || !lineItem.properties) return null;

    var raw = lineItem.properties._cu_step_config;
    if (!raw) return null;

    if (typeof raw === 'object') return raw;

    try {
      return JSON.parse(String(raw));
    } catch (_) {
      return null;
    }
  }

  function normalizeFallbackBehavior(value) {
    return String(value || '').toLowerCase().trim() === 'skip' ? 'skip' : 'global';
  }

  function getLineFallbackBehavior(lineItem, config) {
    if (config && config.fallbackBehavior != null) {
      return normalizeFallbackBehavior(config.fallbackBehavior);
    }

    if (!lineItem || !lineItem.properties) return '';

    var raw = lineItem.properties._cu_step_fallback_behavior;
    return raw == null || raw === '' ? '' : normalizeFallbackBehavior(raw);
  }

  function cartContainsProduct(cart, productId, handle) {
    if (!cart || !cart.items || (!productId && !handle)) return false;

    var wantedProductId = productId ? String(productId) : '';
    var wantedHandle = handle ? String(handle) : '';

    return cart.items.some(function (item) {
      var itemProductId = item && item.product_id != null ? String(item.product_id) : '';
      var itemHandle = item && item.handle ? String(item.handle) : '';
      return !!((wantedProductId && itemProductId === wantedProductId) || (wantedHandle && itemHandle === wantedHandle));
    });
  }

  function createFallbackDecision(config) {
    return normalizeFallbackBehavior(config && config.fallbackBehavior) === 'skip'
      ? createSkipDecision('configured_fallback')
      : createGlobalDecision('configured_fallback');
  }

  function createGlobalDecision(reason) {
    return { action: 'global', reason: reason || 'global' };
  }

  function createSkipDecision(reason) {
    return { action: 'skip', reason: reason || 'skip' };
  }

  function getPageTemplateSourceItem() {
    var source = CONFIG.pageTemplateSource || {};

    return {
      product_id: source.productId || '',
      handle: source.productHandle || ''
    };
  }

  function createTemplateOffer(config, lineItem) {
    var upsell1 = config.upsell1 || {};
    var upsell2 = config.upsell2 || {};

    return {
      type: 'template',
      fallbackBehavior: normalizeFallbackBehavior(config.fallbackBehavior),
      uiVariant: normalizeUiVariant(config.uiVariant || 'upsell1'),
      sourceProductId: lineItem && lineItem.product_id != null ? String(lineItem.product_id) : '',
      sourceProductHandle: lineItem && lineItem.handle ? String(lineItem.handle) : '',
      enabledByVariant: {
        upsell1: upsell1.enabled !== false,
        upsell2: upsell2.enabled !== false
      },
      productHandleByVariant: {
        upsell1: upsell1.productHandle || '',
        upsell2: upsell2.productHandle || ''
      },
      productIdByVariant: {
        upsell1: upsell1.productId || '',
        upsell2: upsell2.productId || ''
      },
      moneyMapByVariant: {
        upsell1: upsell1.moneyMap || {},
        upsell2: upsell2.moneyMap || {}
      },
      shared: config.shared || {},
      settingsByVariant: {
        upsell1: upsell1.settings || {},
        upsell2: upsell2.settings || {}
      }
    };
  }

  function sourceMatchesProduct(sourceItem, productId, handle) {
    if (!sourceItem || (!productId && !handle)) return false;

    var sourceProductId = sourceItem.product_id != null ? String(sourceItem.product_id) : '';
    var sourceHandle = sourceItem.handle ? String(sourceItem.handle) : '';
    var wantedProductId = productId ? String(productId) : '';
    var wantedHandle = handle ? String(handle) : '';

    return !!((wantedProductId && sourceProductId === wantedProductId) || (wantedHandle && sourceHandle === wantedHandle));
  }

  function resolveTemplateConfigDecision(config, sourceItem, cart) {
    if (!config || Number(config.version) !== 1) return null;

    var templateOffer = createTemplateOffer(config, sourceItem);
    if (wasOfferHandledForSession(templateOffer)) {
      return createSkipDecision('handled_session');
    }

    if (config.enabled === false) {
      return createFallbackDecision(config);
    }

    var uiVariant = normalizeUiVariant(config.uiVariant || 'upsell1');
    var variantConfig = config[uiVariant] || {};
    var productHandle = variantConfig.productHandle || '';
    var productId = variantConfig.productId || '';

    if (variantConfig.enabled === false || !productHandle) {
      return createFallbackDecision(config);
    }

    if (sourceMatchesProduct(sourceItem, productId, productHandle)) {
      return createFallbackDecision(config);
    }

    if (cart && cartContainsProduct(cart, productId, productHandle)) {
      return createSkipDecision('duplicate_offer');
    }

    return {
      action: 'template',
      reason: 'valid_template',
      offer: templateOffer
    };
  }

  function resolveCartFallbackDecision(cart) {
    if (!cart || !cart.items || !cart.items.length) {
      return createGlobalDecision('empty_cart');
    }

    var configuredLineCount = 0;
    var fallbackDecision = null;
    var hasSkipFallback = false;

    for (var i = 0; i < cart.items.length; i += 1) {
      var lineItem = cart.items[i];
      var config = parseLineConfig(lineItem);
      var fallbackBehavior = getLineFallbackBehavior(lineItem, config);

      if (!config || Number(config.version) !== 1) {
        if (!fallbackBehavior) continue;

        configuredLineCount += 1;
        if (fallbackBehavior === 'skip') {
          hasSkipFallback = true;
          if (!fallbackDecision) {
            fallbackDecision = createSkipDecision('invalid_config_fallback');
          }
          continue;
        }

        if (!fallbackDecision) {
          fallbackDecision = createGlobalDecision('invalid_config_fallback');
        }
        continue;
      }

      configuredLineCount += 1;
      if (fallbackBehavior === 'skip') {
        hasSkipFallback = true;
      }

      var decision = resolveTemplateConfigDecision(config, lineItem, cart);
      if (!decision) continue;

      if (decision.action === 'skip') {
        return decision;
      }

      if (!fallbackDecision) {
        fallbackDecision = decision;
      }
    }

    if (configuredLineCount > 1) {
      return hasSkipFallback
        ? createSkipDecision('multiple_configured_skip_fallback')
        : createGlobalDecision('multiple_configured_all_global');
    }

    return fallbackDecision || createGlobalDecision('no_config');
  }

  function setCachedCartFallbackDecision(decision, cart) {
    cachedCartFallbackDecision = decision || null;
    cachedCartSnapshot = cachedCartFallbackDecision && cart ? cart : null;
    cachedCartFallbackDecisionAt = cachedCartFallbackDecision ? getNow() : 0;
  }

  function getFreshCartFallbackDecision() {
    if (!cachedCartFallbackDecision || !cachedCartFallbackDecisionAt) return null;
    if (getNow() - cachedCartFallbackDecisionAt > CART_FALLBACK_CACHE_TTL_MS) return null;
    return cachedCartFallbackDecision;
  }

  function setProductSubmitDecision(decision) {
    productSubmitDecision = decision || createGlobalDecision();
    productSubmitDecisionAt = getNow();
  }

  function getProductSubmitDecision() {
    if (!productSubmitDecision || !productSubmitDecisionAt) return null;
    return productSubmitDecision;
  }

  function clearProductSubmitDecision() {
    productSubmitDecision = null;
    productSubmitDecisionAt = 0;
    awaitingPostSubmitCartRefresh = false;
  }

  function invalidateCheckoutDecisionCache(options) {
    cartDecisionGeneration += 1;
    cachedCartFallbackDecision = null;
    cachedCartSnapshot = null;
    cachedCartFallbackDecisionAt = 0;

    if (options && options.clearProduct) {
      clearProductSubmitDecision();
    }
  }

  function refreshCheckoutDecisionCache() {
    var requestGeneration = cartDecisionGeneration;
    if (cartDecisionRequest && cartDecisionRequestGeneration === requestGeneration) {
      return cartDecisionRequest;
    }

    var request = fetchCartJson()
      .then(function (cart) {
        if (requestGeneration !== cartDecisionGeneration) return null;
        if (!cart) {
          setCachedCartFallbackDecision(null);
          return null;
        }

        var decision = resolveCartFallbackDecision(cart);
        setCachedCartFallbackDecision(decision, cart);
        return decision;
      })
      .catch(function () {
        if (requestGeneration === cartDecisionGeneration) {
          setCachedCartFallbackDecision(null);
        }
        return null;
      });

    cartDecisionRequest = request;
    cartDecisionRequestGeneration = requestGeneration;

    request.then(function () {
      if (cartDecisionRequest === request) {
        cartDecisionRequest = null;
        cartDecisionRequestGeneration = -1;
      }
    });

    return request;
  }

  function applyCheckoutDecision(decision) {
    var resolvedDecision = decision || createGlobalDecision();

    if (resolvedDecision.action === 'skip') {
      return false;
    }

    if (resolvedDecision.action === 'template' && resolvedDecision.offer) {
      setActiveOffer(resolvedDecision.offer);
      return true;
    }

    resetActiveOfferToGlobal();
    return true;
  }

  function cartContainsSubmittedTemplateOffer(cart, submittedDecision) {
    if (!cart || !submittedDecision || submittedDecision.action !== 'template' || !submittedDecision.offer) {
      return false;
    }

    var offer = submittedDecision.offer;
    var uiVariant = normalizeUiVariant(offer.uiVariant || 'upsell1');
    var productIdByVariant = offer.productIdByVariant || {};
    var productHandleByVariant = offer.productHandleByVariant || {};

    return cartContainsProduct(
      cart,
      productIdByVariant[uiVariant] || '',
      productHandleByVariant[uiVariant] || ''
    );
  }

  function combineSubmittedAndCartDecision(submittedDecision, cartDecision, cart) {
    if (submittedDecision && submittedDecision.action === 'skip') {
      return submittedDecision;
    }

    if (cartContainsSubmittedTemplateOffer(cart, submittedDecision)) {
      return createSkipDecision('duplicate_offer');
    }

    if (
      cartDecision &&
      cartDecision.action === 'skip' &&
      !(
        submittedDecision &&
        submittedDecision.action === 'template' &&
        cartDecision.reason === 'invalid_config_fallback'
      )
    ) {
      return cartDecision;
    }

    if (submittedDecision && submittedDecision.action === 'global') {
      return submittedDecision;
    }

    if (submittedDecision && submittedDecision.action === 'template') {
      if (!cartDecision || cartDecision.action === 'template') {
        return cartDecision || submittedDecision;
      }

      if (
        cartDecision.action === 'global' &&
        (cartDecision.reason === 'multiple_configured_all_global' || cartDecision.reason === 'configured_fallback')
      ) {
        return cartDecision;
      }

      return submittedDecision;
    }

    return cartDecision || submittedDecision || null;
  }

  function resolveFastCheckoutDecision() {
    var submittedProductDecision = getProductSubmitDecision();
    if (submittedProductDecision) {
      if (submittedProductDecision.action === 'skip') return submittedProductDecision;

      var validatedCartDecision = getFreshCartFallbackDecision();
      if (validatedCartDecision && cachedCartFallbackDecisionAt >= productSubmitDecisionAt) {
        return combineSubmittedAndCartDecision(submittedProductDecision, validatedCartDecision, cachedCartSnapshot);
      }

      return null;
    }

    return getFreshCartFallbackDecision();
  }

  function resolveCheckoutOffer() {
    resetActiveOfferToGlobal();

    var submittedProductDecision = getProductSubmitDecision();
    var fastDecision = resolveFastCheckoutDecision();
    if (fastDecision) {
      return Promise.resolve(applyCheckoutDecision(fastDecision));
    }

    return refreshCheckoutDecisionCache()
      .then(function (decision) {
        if (!decision) {
          throw new Error('Unable to validate checkout upsell decision against cart');
        }

        return applyCheckoutDecision(
          combineSubmittedAndCartDecision(submittedProductDecision, decision, cachedCartSnapshot)
        );
      });
  }

  function parseTemplateConfigInput(input) {
    if (!input || !input.value) return null;

    try {
      return JSON.parse(String(input.value));
    } catch (_) {
      return null;
    }
  }

  function getTemplateProductHandlesForPrefetch() {
    var configInputs = document.querySelectorAll('input[name="properties[_cu_step_config]"]');
    var sourceItem = getPageTemplateSourceItem();
    var handles = {};

    for (var i = 0; i < configInputs.length; i += 1) {
      var config = parseTemplateConfigInput(configInputs[i]);
      var decision = resolveTemplateConfigDecision(config, sourceItem, null);

      if (!decision || decision.action !== 'template' || !decision.offer) continue;

      var offer = decision.offer;
      var handle = offer.productHandleByVariant[offer.uiVariant];
      if (handle) handles[handle] = true;
    }

    return Object.keys(handles);
  }

  function prefetchTemplateProductsAfterPageLoad() {
    function prefetch() {
      getTemplateProductHandlesForPrefetch().forEach(function (handle) {
        fetchProduct(handle);
      });
    }

    function schedulePrefetch() {
      if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(prefetch, { timeout: 1500 });
      } else {
        window.setTimeout(prefetch, 0);
      }
    }

    if (document.readyState === 'complete') {
      schedulePrefetch();
    } else {
      window.addEventListener('load', schedulePrefetch, { once: true });
    }
  }

  function isProductAddToCartForm(form) {
    if (!form || typeof form.getAttribute !== 'function') return false;

    if (form.getAttribute('data-type') === 'add-to-cart-form') return true;

    var action = form.getAttribute('action') || form.action || '';
    return /\/cart\/add(?:\.js)?\/?(?:[?#].*)?$/.test(String(action));
  }

  function handleProductAddToCartSubmit(event) {
    var form = event && event.target;
    if (!form || typeof form.querySelector !== 'function') return;
    if (!isProductAddToCartForm(form)) return;

    var configInput = form.querySelector('input[name="properties[_cu_step_config]"]');
    var fallbackInput = form.querySelector('input[name="properties[_cu_step_fallback_behavior]"]');
    var config = parseTemplateConfigInput(configInput);
    var decision = null;

    if (config && Number(config.version) === 1) {
      decision = resolveTemplateConfigDecision(config, getPageTemplateSourceItem(), null);
    } else if (fallbackInput && normalizeFallbackBehavior(fallbackInput.value) === 'skip') {
      decision = createSkipDecision('invalid_config_fallback');
    }

    decision = decision || createGlobalDecision('missing_config');
    invalidateCheckoutDecisionCache();
    setProductSubmitDecision(decision);
    awaitingPostSubmitCartRefresh = decision.action !== 'skip';
  }

  function bindCartDrawerRefreshObserver() {
    if (cartDrawerObserver || typeof MutationObserver !== 'function') return;

    var drawer = $('cart-drawer');
    if (!drawer) return;

    cartDrawerObserver = new MutationObserver(function () {
      if (!awaitingPostSubmitCartRefresh) return;
      awaitingPostSubmitCartRefresh = false;
      refreshCheckoutDecisionCache();
    });

    cartDrawerObserver.observe(drawer, { childList: true, subtree: true });
  }

  function handlePotentialCartMutation(event) {
    var target = event && event.target;
    if (!target || typeof target.closest !== 'function') return;

    var isDrawerOrCart = !!target.closest('cart-drawer, cart-items');
    if (!isDrawerOrCart) return;

    if (event.type === 'click' && !target.closest('cart-remove-button, .quantity__button')) return;
    if (event.type === 'change' && !target.matches('input[name="updates[]"], .quantity__input, quantity-input input')) return;

    invalidateCheckoutDecisionCache({ clearProduct: true });
    window.setTimeout(refreshCheckoutDecisionCache, 700);
  }

  function getVisibleDialogPanel() {
    var upsell1Panel = $('#cu-step');
    var upsell2Panel = $('#cu-step-v2');

    if (upsell1Panel && upsell1Panel.classList.contains('cu-visible')) return upsell1Panel;
    if (upsell2Panel && upsell2Panel.classList.contains('cu-visible')) return upsell2Panel;

    return null;
  }

  function getDialogFocusableElements(panel) {
    if (!panel) return [];

    var selector = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled]):not([tabindex="-1"])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ].join(',');

    return Array.prototype.slice.call(panel.querySelectorAll(selector)).filter(function (element) {
      if (element.hidden || element.getAttribute('aria-hidden') === 'true') return false;
      if (typeof element.getClientRects !== 'function') return true;
      return element.getClientRects().length > 0;
    });
  }

  function focusVisibleDialogPanel() {
    var focusable = getDialogFocusableElements(getVisibleDialogPanel());
    if (focusable.length && typeof focusable[0].focus === 'function') {
      focusable[0].focus();
    }
  }

  function restoreDialogFocus() {
    var focusTarget = lastFocusedElement;
    lastFocusedElement = null;

    if (focusTarget && focusTarget.isConnected && typeof focusTarget.focus === 'function') {
      focusTarget.focus();
    }
  }

  function openResolvedPanel() {
    setActiveUiVariant(getPreferredUiVariant());

    if (!refs.root || !refs.step) return false;

    if (hideTimer) {
      clearTimeout(hideTimer);
      hideTimer = null;
    }

    setAcceptLoading(false);

    var checkoutTargets = resolveCheckoutTargets();
    lastFocusedElement = checkoutTargets.checkoutBtn || document.activeElement;
    refs.root.setAttribute('aria-hidden', 'false');
    refs.root.classList.add('cu-root-visible');
    document.body.classList.add('cu-step-open');

    requestAnimationFrame(function () {
      if (refs.step) refs.step.classList.add('cu-visible');
      startVariantEnhancements();
      focusVisibleDialogPanel();
    });

    return true;
  }

  function isCurrentShowRequest(requestId) {
    return requestId == null || requestId === showRequestId;
  }

  function cancelShowRequest(requestId) {
    if (requestId == null || requestId === showRequestId) {
      showRequestId += 1;
    }
  }

  function getCheckoutFailOpenTimeout() {
    var configured = parseInt(CONFIG.checkoutFailOpenTimeoutMs, 10);
    if (configured > 0) return configured;
    return CHECKOUT_FAIL_OPEN_TIMEOUT_MS;
  }

  function withCheckoutFailOpenTimeout(promise) {
    return new Promise(function (resolve, reject) {
      var settled = false;
      var timer = setTimeout(function () {
        if (settled) return;
        settled = true;
        reject(new Error('Upsell resolution timed out'));
      }, getCheckoutFailOpenTimeout());

      promise
        .then(function (value) {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          resolve(value);
        })
        .catch(function (error) {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          reject(error);
        });
    });
  }

  function showPanel(requestId) {
    return resolveCheckoutOffer()
      .then(function (shouldShowOffer) {
        if (!isCurrentShowRequest(requestId)) return false;
        if (!shouldShowOffer) return false;
        if (wasUpsellHandledForSession()) return false;

        setActiveUiVariant(getPreferredUiVariant());
        if (!refs.root || !refs.step) return false;

        return preloadVariantProduct(activeUiVariant)
          .then(function (product) {
            if (!isCurrentShowRequest(requestId)) return false;
            if (!product) return false;
            populatePanel(product, activeUiVariant);
            if (!isCurrentShowRequest(requestId)) return false;
            return openResolvedPanel();
          });
      })
      .catch(function () {
        resetActiveOfferToGlobal();
        return false;
      });
  }

  function hidePanel(callback) {
    if (!refs.root) {
      stopVariantEnhancements();
      restoreDialogFocus();
      if (callback) callback();
      return;
    }

    var upsell1Panel = $('#cu-step');
    var upsell2Panel = $('#cu-step-v2');
    var isVisible = !!((upsell1Panel && upsell1Panel.classList.contains('cu-visible')) || (upsell2Panel && upsell2Panel.classList.contains('cu-visible')));

    if (!isVisible) {
      refs.root.classList.remove('cu-root-visible');
      refs.root.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('cu-step-open');
      stopVariantEnhancements();
      restoreDialogFocus();
      if (callback) callback();
      return;
    }

    if (hideTimer) {
      clearTimeout(hideTimer);
      hideTimer = null;
    }

    if (upsell1Panel) upsell1Panel.classList.remove('cu-visible');
    if (upsell2Panel) upsell2Panel.classList.remove('cu-visible');
    refs.root.setAttribute('aria-hidden', 'true');

    stopVariantEnhancements();

    hideTimer = setTimeout(function () {
      refs.root.classList.remove('cu-root-visible');
      document.body.classList.remove('cu-step-open');
      hideTimer = null;
      restoreDialogFocus();
      if (callback) callback();
    }, 340);
  }

  function refreshCartDrawerUI() {
    var drawer = $('cart-drawer');
    if (!drawer || typeof drawer.renderContents !== 'function') {
      return Promise.resolve();
    }

    var cartUrl = (window.routes && window.routes.cart_url) ? window.routes.cart_url : '/cart';
    var sectionsUrl = cartUrl + '?sections=cart-drawer,cart-icon-bubble';

    return fetch(sectionsUrl, { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (sections) {
        if (!sections || !sections['cart-drawer'] || !sections['cart-icon-bubble']) return;
        drawer.renderContents({
          id: Date.now(),
          sections: sections
        });
      })
      .catch(function () {});
  }

  function resolveCheckoutTargets() {
    var ctx = window.__cuStepCheckoutContext || {};
    var cartForm = null;
    var checkoutBtn = null;

    if (ctx.formId) {
      cartForm = document.getElementById(ctx.formId);
    }

    if (!cartForm) {
      cartForm = $('#CartDrawer-Form') || $('#cart');
    }

    if (ctx.submitterId) {
      checkoutBtn = document.getElementById(ctx.submitterId);
    }

    if (!checkoutBtn && cartForm && ctx.submitterName) {
      checkoutBtn = cartForm.querySelector('[name="' + ctx.submitterName + '"]');
    }

    if (!checkoutBtn) {
      checkoutBtn = $('#CartDrawer-Checkout') || $('#checkout') || (cartForm && cartForm.querySelector('[name="checkout"]'));
    }

    return {
      cartForm: cartForm,
      checkoutBtn: checkoutBtn
    };
  }

  function proceedToCheckout() {
    var targets = resolveCheckoutTargets();
    var cartForm = targets.cartForm;
    var checkoutBtn = targets.checkoutBtn;

    window.__cuStepBypass = true;
    setTimeout(function () { window.__cuStepBypass = false; }, 3000);

    if (checkoutBtn && !cartForm && checkoutBtn.form) {
      cartForm = checkoutBtn.form;
    }

    if (!cartForm) {
      window.location.assign('/checkout');
      return;
    }

    if (typeof cartForm.requestSubmit === 'function') {
      if (checkoutBtn && checkoutBtn.form === cartForm) {
        cartForm.requestSubmit(checkoutBtn);
      } else {
        cartForm.requestSubmit();
      }
    } else {
      cartForm.submit();
    }

    window.__cuStepCheckoutContext = null;
  }

  function getSelectedVariantId() {
    var storedId = getStoredVariantId(activeUiVariant);
    if (storedId) return storedId;

    if (refs.variantSelect && refs.variantSelect.value) {
      return String(refs.variantSelect.value);
    }

    var product = getActiveProductData();
    if (product && product.variants && product.variants[0]) {
      return String(product.variants[0].id);
    }

    return '';
  }

  function seedTemplateSkipDecision(reason, requireOncePerSession) {
    if (!isTemplateOfferActive()) return;
    if (requireOncePerSession && !shouldUseOncePerSession()) return;

    var skipDecision = createSkipDecision(reason);
    invalidateCheckoutDecisionCache({ clearProduct: true });
    setProductSubmitDecision(skipDecision);
    setCachedCartFallbackDecision(skipDecision);
  }

  function handleAccept() {
    var variantId = getSelectedVariantId();

    if (!variantId) {
      markUpsellHandledForSession();
      seedTemplateSkipDecision('handled_session', true);
      hidePanel(function () {
        proceedToCheckout();
      });
      return;
    }

    setAcceptLoading(true);

    addVariantToCart(variantId)
      .then(function () {
        markUpsellHandledForSession();
        seedTemplateSkipDecision('duplicate_offer', false);

        hidePanel(function () {
          proceedToCheckout();
        });
      })
      .catch(function (err) {
        if (err) {
          console.error('[Upsell Step] add-to-cart failed:', err.message || err);
        }
        setAcceptLoading(false);
      });
  }

  function handleDecline() {
    markUpsellHandledForSession();
    seedTemplateSkipDecision('handled_session', true);
    hidePanel(function () {
      proceedToCheckout();
    });
  }

  function handleClose() {
    markUpsellHandledForSession();
    seedTemplateSkipDecision('handled_session', true);
    hidePanel(function () {
      proceedToCheckout();
    });
  }

  function handleDialogKeydown(e) {
    var panel = getVisibleDialogPanel();
    if (!panel) return;

    if (e.key === 'Escape') {
      e.preventDefault();
      handleClose();
      return;
    }

    if (e.key !== 'Tab') return;

    var focusable = getDialogFocusableElements(panel);
    if (!focusable.length) {
      e.preventDefault();
      panel.setAttribute('tabindex', '-1');
      panel.focus();
      return;
    }

    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    var active = document.activeElement;

    if (e.shiftKey && (active === first || !panel.contains(active))) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && (active === last || !panel.contains(active))) {
      e.preventDefault();
      first.focus();
    }
  }

  function bindEvents() {
    if (hasBoundEvents) return;
    hasBoundEvents = true;

    ['#cu-accept-btn', '#cu2-accept-btn'].forEach(function (sel) {
      var btn = $(sel);
      if (btn) btn.addEventListener('click', handleAccept);
    });

    ['#cu-decline-btn', '#cu2-decline-btn'].forEach(function (sel) {
      var btn = $(sel);
      if (btn) btn.addEventListener('click', handleDecline);
    });

    ['#cu-close-btn', '#cu2-close-btn'].forEach(function (sel) {
      var btn = $(sel);
      if (btn) btn.addEventListener('click', handleClose);
    });

    ['#cu-variant-select', '#cu2-variant-select'].forEach(function (sel) {
      var selectEl = $(sel);
      if (!selectEl) return;
      selectEl.addEventListener('change', function () {
        var uiVariant = sel === '#cu2-variant-select' ? 'upsell2' : 'upsell1';
        applyVariantState(selectEl.value, uiVariant);
      });
    });

    var toggle = $('#cu-step-dev-toggle');
    if (toggle) {
      toggle.addEventListener('click', function (event) {
        var btn = event.target.closest('[data-cu-variant-choice]');
        if (!btn) return;

        var chosen = normalizeUiVariant(btn.getAttribute('data-cu-variant-choice') || 'upsell1');
        if (!isVariantEnabled(chosen)) return;
        persistDebugUiVariant(chosen);
        setActiveUiVariant(chosen);
      });
    }

    document.addEventListener('submit', handleProductAddToCartSubmit, true);
    document.addEventListener('click', handlePotentialCartMutation, true);
    document.addEventListener('change', handlePotentialCartMutation, true);
    document.addEventListener('keydown', handleDialogKeydown);
    bindCartDrawerRefreshObserver();
  }

  window.cuStepShow = function () {
    var requestId = showRequestId + 1;
    showRequestId = requestId;

    withCheckoutFailOpenTimeout(showPanel(requestId))
      .then(function (opened) {
        if (!isCurrentShowRequest(requestId)) return;
        if (!opened) {
          cancelShowRequest(requestId);
          proceedToCheckout();
        }
      })
      .catch(function () {
        cancelShowRequest(requestId);
        proceedToCheckout();
      });
  };

  function init() {
    setActiveUiVariant(getPreferredUiVariant());

    if (!refs.root || !refs.step) return;

    if (isVariantEnabled('upsell1')) {
      preloadVariantProduct('upsell1');
    }

    if (isVariantEnabled('upsell2')) {
      preloadVariantProduct('upsell2');
    }

    bindEvents();
    refreshCheckoutDecisionCache();
    prefetchTemplateProductsAfterPageLoad();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
